'use client';
import React from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import styles from './listennow.module.scss';
import { PULSE } from '@/constants/pulse/pulse';
import SiriusLogoBlack from '@/assets/icons/sxm-logo-black.svg';
import SiriusLogo from '@/assets/icons/icon-sirius-logo.svg';
import BlackArrow from '@/assets/icons/icon-black-arrow.svg';
import BlueArrow from '@/assets/icons/icon-blue-arrow.svg';
import WhiteArrow from '@/assets/icons/icon-white-arrow.svg';
import CarDashboard from '@/assets/icons/icon-car-dash.svg';
import Mobile from '@/assets/icons/icon-mobile-outline.svg';
import { getPulseAnalyticsAttributes, buildAppLinkUrlAllParams } from '@/packages/pulse';
import { showConversionView } from '../StaticContent/pulse/Pricing/GenerateOffer';
import SXMLink from '@/shared-components/SXMLink';
import ImageWrapper from '@/shared-components/ImageWrapper/ImageWrapper';
const PrimaryCallToAction = dynamic(() => import('./PrimaryCallToAction'));

interface ListenNowProps {
    index?: number;
    asset: SiteAssetGroup;
}

const ListenNow = ({ asset }: ListenNowProps) => {
    const listenNowContent = asset.fields.assets && asset.fields.assets[0];
    const backgroundImageUrl = listenNowContent?.fields.backgroundImage.url;
    const { href, link_target, link_text, analytics_tags, inheritUrlParameters } =
        listenNowContent?.fields.moreLink.fields;
    const {
        blueArrowAltText,
        blackArrowAltText,
        whiteArrowAltText,
        carDashboardAltText,
        appLinkUrl,
        appLinkText,
        appTitle,
        appText,
        mobileAltText,
        backgroundAltText,
        blackLogoAltText,
        blueLogoAltText,
        carTitle,
        carText,
    } = PULSE;

    const analyticsAttr = getPulseAnalyticsAttributes(analytics_tags);
    const searchParams = useSearchParams();
    const showConversion = showConversionView(searchParams);
    const appLinkHref = buildAppLinkUrlAllParams(appLinkUrl, searchParams);

    return (
        <>
            <div className={cn(styles.listen_now, { [styles.conversion]: showConversion })}>
                {/* Hide on mobile conversion; show on mobile & desktop discovery */}
                <div className={styles.stella_cta_wrapper}>
                    <div className={styles.stella_wrapper}>
                        <ImageWrapper
                            src="/content/dam/sxm-com/pulse/stella-alert.png"
                            alt="Stella Alert"
                            optimizedWidth={100}
                            optimizedHeight={100}
                            loadingStrategy="eager"
                            skipOptimization={true}
                        />
                    </div>
                    <div className={styles.main_cta_wrapper}>
                        <PrimaryCallToAction showConversion={showConversion} appLinkHref={appLinkHref} />
                    </div>
                </div>
                {/* Mobile Conversion CTA */}
                <div className={styles.mobile_cta_wrapper}>
                    <PrimaryCallToAction showConversion={showConversion} appLinkHref={appLinkHref} />
                </div>
                <div className={styles.main_content_wrapper}>
                    <div className={styles.listen_now_cohort_content_container}>
                        <div className={styles.listen_now_mobile_cohort_background}>
                            <div className={styles.listen_now_cohort_background}>
                                <ImageWrapper
                                    src={backgroundImageUrl}
                                    alt={backgroundAltText}
                                    className={styles.listen_now_cohort_background_image}
                                    optimizedWidth={1664}
                                    optimizedHeight={450}
                                />
                            </div>
                        </div>
                        <div className={styles.content_layout_mobile}>
                            <div className={styles.listen_now_cohort_content}>
                                <div className={styles.listen_now_cohort_content_title}>
                                    {listenNowContent?.fields.title}
                                </div>
                                <div className={styles.listen_now_cohort_content_text}>
                                    {listenNowContent?.fields.description}
                                </div>
                            </div>
                            {/* Mobile Sirius Logo */}
                            <div className={styles.sirius_logo_mobile_wrapper}>
                                <div className={styles.sirius_logo_mobile_image_container}>
                                    <SiriusLogoBlack
                                        className={`${styles.sirius_logo_image} ${styles.black_background}`}
                                        alt={blackLogoAltText}
                                    />
                                </div>
                                <div className={styles.teal_box}></div>
                            </div>
                        </div>
                        {/* Desktop Sirius Logo */}
                        <div className={styles.sirius_logo_wrapper}>
                            <div className={styles.white_box}></div>
                            <div className={styles.sirius_logo_container}>
                                <SiriusLogo className={styles.sirius_logo_image} alt={blueLogoAltText} />
                            </div>
                        </div>
                        <div className={styles.cohort_channel_reference}>
                            <div className={styles.color_box}>
                                <div className={styles.black_box}></div>
                                <div className={styles.yellow_box}></div>
                            </div>
                            {/* Desktop Channel Button */}
                            <SXMLink
                                className={`${styles.view_more_link} ${styles.appLinkHref}`}
                                href={href}
                                target={link_target}
                                inheritParams={inheritUrlParameters}
                                {...analyticsAttr}
                            >
                                <div className={styles.cohort_channel_button}>
                                    <div className={styles.cohort_channel_button_text}>{link_text}</div>
                                    <BlackArrow className={styles.arrow_image} alt={blackArrowAltText} />
                                </div>
                            </SXMLink>
                        </div>
                    </div>
                    <div className={cn(styles.listen_now_wrapper, { [styles.hidden]: showConversion })}>
                        <div className={styles.listen_now_container}>
                            <div className={styles.car}>
                                <div className={styles.text_wrap_mobile}>
                                    <div className={styles.list_title}>{carTitle}</div>
                                    <div className={styles.list_text}>{carText}</div>
                                </div>
                                <div className={styles.image_text_wrapper}>
                                    <div className={styles.car_image_wrapper}>
                                        <CarDashboard className={styles.car_image} alt={carDashboardAltText} />
                                    </div>
                                    <div className={styles.list_text_desktop}>{carText}</div>
                                </div>
                            </div>
                            <div className={styles.app}>
                                <div className={styles.app_block}>
                                    <div className={styles.text_wrap_mobile}>
                                        <div className={styles.list_title}>{appTitle}</div>
                                        <div className={styles.list_text}>{appText}</div>
                                        <div className={styles.link_mobile}>
                                            <SXMLink
                                                className={styles.appLinkHref}
                                                href={appLinkHref}
                                                inheritParams={true}
                                                data-component="listenNow"
                                            >
                                                <div className={styles.listen_now_link_wrapper}>{appLinkText}</div>
                                                <BlueArrow className={styles.arrow_image} alt={blueArrowAltText} />
                                            </SXMLink>
                                        </div>
                                    </div>
                                    <div className={styles.image_text_wrapper}>
                                        <div className={styles.mobile_image_wrapper}>
                                            <Mobile className={styles.mobile_image} alt={mobileAltText} />
                                        </div>
                                        <div className={styles.desktop_image_wrapper}>
                                            <ImageWrapper
                                                src="/resources/images/mobile-icon-small.svg"
                                                alt={mobileAltText}
                                                className={styles.mobile_image}
                                                optimizedWidth={106}
                                                optimizedHeight={60}
                                            />
                                        </div>
                                        <div className={styles.list_text_desktop}>{appText}</div>
                                    </div>
                                </div>
                                <div className={styles.link}>
                                    <SXMLink
                                        className={styles.appLinkHref}
                                        href={appLinkHref}
                                        inheritParams={true}
                                        data-component="listenNow"
                                    >
                                        <div className={styles.listen_now_link_wrapper}>{appLinkText}</div>
                                        <BlueArrow className={styles.arrow_image} alt={blueArrowAltText} />
                                    </SXMLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Mobile Channel Button */}
                <div className={styles.cohort_channel_button_mobile}>
                    <SXMLink
                        className={`${styles.view_more_link} ${styles.appLinkHref}`}
                        href={href}
                        inheritParams={inheritUrlParameters}
                        target={link_target}
                        {...analyticsAttr}
                    >
                        <div className={styles.cohort_channel_button_text}>{link_text}</div>
                        <WhiteArrow className={styles.arrow_image} alt={whiteArrowAltText} />
                    </SXMLink>
                </div>
                <div className={styles.lower_margin}>
                    <div className={styles.lower_margin_blue} />
                </div>
            </div>
        </>
    );
};

export default ListenNow;
